<template>
    <div class="promotion-panel invitations-panel">
        <div class="panel-heading">
            <div>
                <h2>邀请关系管理</h2>
                <p>作废和恢复都必须填写原因，操作成功后服务端会记录操作人、原因和时间。</p>
            </div>
            <el-button icon="el-icon-refresh" @click="loadInvitations">刷新列表</el-button>
        </div>

        <div class="filter-card">
            <el-form :inline="true" size="small" @submit.native.prevent="loadInvitations">
                <el-form-item label="学校">
                    <el-select v-model="filters.schoolId" clearable placeholder="全部" @change="loadBuildings">
                        <el-option
                            v-for="school in schools"
                            :key="school.schoolId || school.id"
                            :label="school.name"
                            :value="school.schoolId || school.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="宿舍楼">
                    <el-select v-model="filters.buildingId" clearable placeholder="全部">
                        <el-option
                            v-for="building in buildings"
                            :key="building.buildingId || building.id"
                            :label="building.name"
                            :value="building.buildingId || building.id" />
                    </el-select>
                </el-form-item>
                <el-form-item label="楼长">
                    <el-input v-model.trim="filters.leaderUserId" clearable placeholder="楼长用户 ID" />
                </el-form-item>
                <el-form-item label="状态">
                    <el-select v-model="filters.status" clearable placeholder="全部">
                        <el-option label="有效" value="active" />
                        <el-option label="已作废" value="revoked" />
                    </el-select>
                </el-form-item>
                <el-form-item label="来源">
                    <el-select v-model="filters.sourceType" clearable placeholder="全部">
                        <el-option label="楼长推广" value="building_leader" />
                        <el-option label="普通邀请" value="invite_code" />
                    </el-select>
                </el-form-item>
                <el-form-item label="注册时间">
                    <el-date-picker
                        v-model="dateRange"
                        type="daterange"
                        range-separator="至"
                        start-placeholder="开始日期"
                        end-placeholder="结束日期"
                        value-format="yyyy-MM-dd" />
                </el-form-item>
                <el-form-item>
                    <el-button type="primary" icon="el-icon-search" @click="loadInvitations">查询</el-button>
                    <el-button @click="resetFilters">重置</el-button>
                </el-form-item>
            </el-form>
        </div>

        <el-table v-loading="loading" :data="invitations" stripe empty-text="暂无邀请关系">
            <el-table-column label="被邀请用户" min-width="145">
                <template slot-scope="scope">
                    <div>{{ scope.row.inviteeName || scope.row.userName || scope.row.inviteeUserId || '-' }}</div>
                    <small class="muted">用户 ID：{{ scope.row.inviteeUserId || scope.row.userId || '-' }}</small>
                </template>
            </el-table-column>
            <el-table-column label="学校 / 宿舍楼" min-width="170">
                <template slot-scope="scope">
                    {{ scope.row.schoolName || '-' }}
                    <span v-if="scope.row.buildingName"> / {{ scope.row.buildingName }}</span>
                </template>
            </el-table-column>
            <el-table-column label="楼长" min-width="130">
                <template slot-scope="scope">{{ scope.row.leaderName || scope.row.leaderUserName || scope.row.leaderUserId || '-' }}</template>
            </el-table-column>
            <el-table-column label="状态" width="85">
                <template slot-scope="scope">
                    <el-tag :type="isActive(scope.row) ? 'success' : 'info'" size="mini">
                        {{ isActive(scope.row) ? '有效' : '已作废' }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column label="来源" width="110">
                <template slot-scope="scope">{{ sourceLabel(scope.row.sourceType) }}</template>
            </el-table-column>
            <el-table-column label="注册时间" min-width="155">
                <template slot-scope="scope">{{ scope.row.registeredAt || scope.row.createdAt || '-' }}</template>
            </el-table-column>
            <el-table-column label="奖励状态" width="100">
                <template slot-scope="scope">
                    <el-tag :type="scope.row.rewardIssued ? 'success' : 'info'" size="mini">
                        {{ scope.row.rewardIssued ? '已发放' : '未发放' }}
                    </el-tag>
                </template>
            </el-table-column>
            <el-table-column label="操作" width="100" fixed="right">
                <template slot-scope="scope">
                    <el-button type="text" @click="openOperation(scope.row)">
                        {{ isActive(scope.row) ? '作废' : '恢复' }}
                    </el-button>
                </template>
            </el-table-column>
        </el-table>

        <el-dialog :title="operationTitle" :visible.sync="operationDialogVisible" width="460px">
            <el-alert
                :title="operationActive ? '作废后该邀请关系不再计入有效统计。' : '恢复后重新计入有效统计，但不会重复发放奖励。'"
                type="warning"
                :closable="false"
                show-icon
                class="operation-alert" />
            <el-form ref="operationForm" :model="operationForm" :rules="operationRules" label-width="80px">
                <el-form-item label="操作原因" prop="reason">
                    <el-input v-model.trim="operationForm.reason" type="textarea" :rows="4" maxlength="200" show-word-limit placeholder="请输入原因" />
                </el-form-item>
            </el-form>
            <span slot="footer">
                <el-button @click="operationDialogVisible = false">取消</el-button>
                <el-button :type="operationActive ? 'danger' : 'warning'" :loading="saving" @click="submitOperation">
                    确认{{ operationActive ? '作废' : '恢复' }}
                </el-button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import {
    asList,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";

const jsonConfig = { headers: { "Content-Type": "application/json" } };

export default {
    data() {
        return {
            schools: [],
            buildings: [],
            invitations: [],
            dateRange: [],
            filters: {
                schoolId: "",
                buildingId: "",
                leaderUserId: "",
                status: "",
                sourceType: "",
            },
            loading: false,
            saving: false,
            operationDialogVisible: false,
            operationActive: true,
            operationForm: { invitationId: "", reason: "" },
            operationRules: {
                reason: [{ required: true, message: "请输入操作原因", trigger: "blur" }],
            },
        };
    },
    computed: {
        operationTitle() {
            return this.operationActive ? "作废邀请关系" : "恢复邀请关系";
        },
    },
    mounted() {
        this.loadSchools();
        this.loadInvitations();
    },
    methods: {
        idOf(item, key) {
            return item && (item[key] || item.id);
        },
        isActive(item) {
            return item && (item.status === "active" || item.status === 1 || item.status === "1" || item.active === true);
        },
        sourceLabel(sourceType) {
            return sourceType === "building_leader" ? "楼长推广" : sourceType === "invite_code" ? "普通邀请" : sourceType || "-";
        },
        loadSchools() {
            request.get(API.promotion.schools.list, { params: { status: 1 } })
                .then((response) => {
                    if (isResultSuccess(response)) this.schools = asList(response);
                    else this.$message.error(getResultMessage(response, "学校列表加载失败"));
                })
                .catch((error) => this.$message.error(getResultMessage(error, "学校列表加载失败")));
        },
        loadBuildings() {
            this.filters.buildingId = "";
            if (!this.filters.schoolId) {
                this.buildings = [];
                return;
            }
            request.get(API.promotion.buildings.list, { params: { schoolId: this.filters.schoolId, status: 1 } })
                .then((response) => {
                    if (isResultSuccess(response)) this.buildings = asList(response);
                    else this.$message.error(getResultMessage(response, "宿舍楼列表加载失败"));
                })
                .catch((error) => this.$message.error(getResultMessage(error, "宿舍楼列表加载失败")));
        },
        buildSearchPayload() {
            const payload = {};
            Object.keys(this.filters).forEach((key) => {
                if (this.filters[key] !== "" && this.filters[key] !== null && this.filters[key] !== undefined) payload[key] = this.filters[key];
            });
            if (this.dateRange && this.dateRange.length === 2) {
                payload.startDate = this.dateRange[0];
                payload.endDate = this.dateRange[1];
            }
            return payload;
        },
        loadInvitations() {
            this.loading = true;
            request.post(API.promotion.invitations.search, this.buildSearchPayload(), jsonConfig)
                .then((response) => {
                    if (!isResultSuccess(response)) {
                        this.$message.error(getResultMessage(response, "邀请关系列表加载失败"));
                        return;
                    }
                    this.invitations = asList(response);
                })
                .catch((error) => this.$message.error(getResultMessage(error, "邀请关系列表加载失败")))
                .finally(() => {
                    this.loading = false;
                });
        },
        resetFilters() {
            this.filters = { schoolId: "", buildingId: "", leaderUserId: "", status: "", sourceType: "" };
            this.dateRange = [];
            this.buildings = [];
            this.loadInvitations();
        },
        openOperation(invitation) {
            this.operationActive = this.isActive(invitation);
            this.operationForm = { invitationId: this.idOf(invitation, "invitationId"), reason: "" };
            this.operationDialogVisible = true;
        },
        submitOperation() {
            this.$refs.operationForm.validate((valid) => {
                if (!valid) return;
                const pathBuilder = this.operationActive
                    ? API.promotion.invitations.revoke
                    : API.promotion.invitations.restore;
                this.saving = true;
                request.post(pathBuilder(this.operationForm.invitationId), {
                    reason: this.operationForm.reason,
                }, jsonConfig)
                    .then((response) => {
                        if (!isResultSuccess(response)) {
                            this.$message.error(getResultMessage(response, "邀请关系操作失败"));
                            return;
                        }
                        this.$message.success(this.operationActive ? "邀请关系已作废" : "邀请关系已恢复");
                        this.operationDialogVisible = false;
                        this.loadInvitations();
                    })
                    .catch((error) => this.$message.error(getResultMessage(error, "邀请关系操作失败")))
                    .finally(() => {
                        this.saving = false;
                    });
            });
        },
    },
};
</script>

<style scoped>
.panel-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 20px;
}

.panel-heading h2 {
    margin: 0;
    font-size: 22px;
}

.panel-heading p {
    margin: 8px 0 0;
    color: #909399;
}

.filter-card {
    padding: 18px 20px 4px;
    margin-bottom: 20px;
    background: #f8f9fc;
    border: 1px solid #ebeef5;
    border-radius: 8px;
}

.filter-card >>> .el-form-item {
    margin-bottom: 14px;
}

.muted {
    color: #909399;
}

.operation-alert {
    margin-bottom: 20px;
}

@media (max-width: 700px) {
    .panel-heading {
        display: block;
    }

    .panel-heading .el-button {
        margin-top: 16px;
    }
}
</style>
